import {type FC, useEffect} from 'react';
import useModal from '../../../hooks/useModal.tsx';
import type {PopupItem, PopupType} from '../index';
import BellSVG from '../../../assets/common/bell.svg';
import NoticeSVG from '../../../assets/common/notice.svg';
import CallSVG from '../../../assets/common/call.svg';
import {Button, ButtonsWrapper, Container, IconWrapper, Overlay, TextContent} from './Popup.styled.ts';
import {FocusContext, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import useGlobalStore from '../../../store/useGlobalStore.ts';
import {useLocation} from 'react-router';

interface PopupProps {
    item: PopupItem;
}

const getIcon = (type: PopupType) => {
    switch (type) {
        case 'message':
            return <img src={BellSVG} alt={'bell'} style={{width: '100%'}} />;
        case 'notice':
            return <img src={NoticeSVG} alt={'notice'} style={{width: '100%'}} />;
        case 'video_call':
            return <img src={CallSVG} alt={'call'} style={{width: '100%'}} />;
        default:
            return null;
    }
};

const Popup: FC<PopupProps> = ({item}) => {
    const {stack} = useGlobalStore();
    const {close, closeAll} = useModal();
    const {popupType, data, id, zIndex} = item;

    const title = data.title || (popupType === 'video_call' ? '영상 통화 요청' : '알림');
    const content =
        data.content ||
        (popupType === 'video_call' ? `${data.center_name ?? ''}에서 영상 통화 요청이 왔습니다.` : '내용 없음');

    const location = useLocation();
    const getCurrentMenu = () => {
        const path = location.pathname.slice(1);
        return path || 'streaming';
    };

    const handleClose = () => {
        const nextIndex = stack.findIndex((stackItem) => stackItem.id === item.id) - 1;
        if (nextIndex >= 0) {
            setFocus(`${stack[nextIndex].id}_CLOSE_BUTTON`);
        }
        if (stack.length === 1) {
            setFocus(getCurrentMenu());
        }
        close(id);
    };

    const handleConfirm = () => {
        if (item.onConfirm) {
            item.onConfirm();
        }
        close(id);
        closeAll();
    };

    const {
        ref: containerRef,
        focusKey,
        focusSelf: focusContainer,
    } = useFocusable({
        focusable: true,
        isFocusBoundary: true,
        focusBoundaryDirections: ['left', 'right'],
        trackChildren: true,
        autoRestoreFocus: false,
        preferredChildFocusKey: `${item.id}_CLOSE_BUTTON`,
    });

    const {ref: confirmRef, focused: confirmFocused} = useFocusable({
        focusKey: `${item.id}_CONFIRM_BUTTON`,
        onEnterPress: handleConfirm,
        onArrowPress: (direction) => {
            if (direction === 'right') {
                setFocus(`${item.id}_CLOSE_BUTTON`);
                return false;
            }
            return false;
        },
    });

    const {
        ref: closeRef,
        focused: closeFocused,
        focusSelf: focusClose,
    } = useFocusable({
        focusKey: `${item.id}_CLOSE_BUTTON`,
        onEnterPress: handleClose,
        onArrowPress: (direction) => {
            if (direction === 'left' && (popupType === 'message' || popupType === 'video_call')) {
                setFocus(`${item.id}_CONFIRM_BUTTON`);
                return false;
            }
            return false;
        },
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            focusContainer();
            requestAnimationFrame(() => {
                focusClose();
            });
        }, 100);
        return () => {
            clearTimeout(timer);
        };
    }, [focusContainer, focusClose]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, []);

    return (
        <Overlay $ZIndex={zIndex}>
            <FocusContext.Provider value={focusKey}>
                <Container ref={containerRef}>
                    <IconWrapper>{getIcon(popupType)}</IconWrapper>
                    <TextContent>
                        <h2>{title}</h2>
                        <p>{content}</p>
                    </TextContent>

                    <ButtonsWrapper>
                        {(popupType === 'message' || popupType === 'video_call') && (
                            <Button ref={confirmRef} $focused={confirmFocused} onClick={handleConfirm}>
                                확인
                            </Button>
                        )}
                        <Button ref={closeRef} $focused={closeFocused} onClick={handleClose}>
                            닫기
                        </Button>
                    </ButtonsWrapper>
                </Container>
            </FocusContext.Provider>
        </Overlay>
    );
};

export default Popup;
